import type {
    AddressLink,
    DocumentLink,
    EmailLink,
    ExternalLink,
    Link,
    PageLink,
    PhoneLink,
    Rel,
    Target,
    WhatsAppLink
} from '@wix/document-services-types'
import _ from 'lodash'

type Attributes = Record<string, any>
type Arguments = Record<string, string | undefined>
interface HRef {
    content: string
    urlScheme?: string
    args?: Arguments
}

interface ParsedAttributes {
    href: HRef
    rel?: Rel
    target?: Target
}

const REL_DELIMITER = ' '
const ARGS_DELIMITER = '&'

const SCHEME_BY_TYPE = {
    AddressLink: 'address',
    DocumentLink: 'document',
    EmailLink: 'mailto',
    PageLink: 'page',
    PhoneLink: 'tel',
    WhatsAppLink: 'whatsapp'
}

const TYPE_BY_SCHEME = _.invert(SCHEME_BY_TYPE)

const stringifyHref = ({urlScheme, content, args}: HRef): string => {
    const stringifiedHref = []

    if (urlScheme) {
        stringifiedHref.push(`${urlScheme}:`)
    }

    stringifiedHref.push(content)

    if (args && Object.keys(_.omitBy(args, _.isUndefined)).length > 0) {
        stringifiedHref.push(`?${_.map(args, (value: string, key: string) => `${key}=${value}`).join(ARGS_DELIMITER)}`)
    }

    return stringifiedHref.join('')
}

const parseHref = (href: string): HRef | undefined => {
    const [match, urlScheme, content, argsStr] = href.match(/^(?:(.+?)\:)?(.+?)(?:\?(.+=.+))?$/) ?? []
    if (!match) {
        return
    }

    const args = argsStr && Object.fromEntries(argsStr.split(ARGS_DELIMITER).map(argStr => argStr.split('=')))

    return {
        content,
        urlScheme,
        args
    }
}

const stringifyRel = (rel?: Rel): string | undefined => {
    if (_.isNil(rel)) {
        return
    }

    return rel.join(REL_DELIMITER)
}

const parseRel = (rel?: string): Rel | undefined => {
    if (_.isNil(rel)) {
        return
    }

    if (_.isEmpty(rel)) {
        return []
    }

    return rel.split(REL_DELIMITER) as Rel
}

const HANDLERS = {
    ExternalLink: {
        pack: ({url, rel, target}: ExternalLink): Attributes => {
            const attributes: Attributes = {href: url}

            const stringifiedRel = stringifyRel(rel)
            if (stringifiedRel !== undefined) {
                attributes.rel = stringifiedRel
            }

            if (target) {
                attributes.target = target
            }

            return attributes
        },
        unpack: (attributes: Attributes, {rel, target}: ParsedAttributes): ExternalLink | undefined => {
            const {href} = attributes

            delete attributes.href
            delete attributes.rel
            delete attributes.target

            return {url: href, rel, target, type: 'ExternalLink'}
        }
    },
    AddressLink: {
        pack: ({address}: AddressLink): Attributes => ({
            href: stringifyHref({urlScheme: SCHEME_BY_TYPE.AddressLink, content: address})
        }),
        unpack: (attributes: Attributes, {href}: ParsedAttributes): AddressLink | undefined => {
            delete attributes.href

            return {address: href.content, type: 'AddressLink'}
        }
    },
    DocumentLink: {
        pack: ({docId, name, indexable}: DocumentLink): Attributes => {
            const args: Arguments = {name}

            if (indexable) {
                args.indexable = indexable.toString()
            }

            return {
                href: stringifyHref({urlScheme: SCHEME_BY_TYPE.DocumentLink, content: docId, args})
            }
        },
        unpack: (attributes: Attributes, {href}: ParsedAttributes): DocumentLink | undefined => {
            delete attributes.href

            const {content, args} = href

            return {
                docId: content,
                name: args?.name,
                indexable: args?.indexable === 'true',
                type: 'DocumentLink'
            } as DocumentLink
        }
    },
    EmailLink: {
        pack: ({recipient, subject, body}: EmailLink): Attributes => {
            const args: Arguments = {}

            if (subject) {
                args.subject = subject
            }
            if (body) {
                args.body = body
            }

            return {
                href: stringifyHref({urlScheme: SCHEME_BY_TYPE.EmailLink, content: recipient, args})
            }
        },
        unpack: (attributes: Attributes, {href}: ParsedAttributes): EmailLink | undefined => {
            delete attributes.href

            const {content, args} = href

            return {recipient: content, subject: args?.subject, body: args?.body, type: 'EmailLink'}
        }
    },
    PageLink: {
        pack: ({pageId, rel, target}: PageLink): Attributes => {
            const attributes: Attributes = {href: stringifyHref({urlScheme: SCHEME_BY_TYPE.PageLink, content: pageId})}

            const stringifiedRel = stringifyRel(rel)
            if (stringifiedRel !== undefined) {
                attributes.rel = stringifiedRel
            }

            if (target) {
                attributes.target = target
            }

            return attributes
        },
        unpack: (attributes: Attributes, {href, rel, target}: ParsedAttributes): PageLink | undefined => {
            delete attributes.href
            delete attributes.rel
            delete attributes.target

            return {pageId: href.content, rel, target, type: 'PageLink'}
        }
    },
    PhoneLink: {
        pack: ({phoneNumber}: PhoneLink): Attributes => ({
            href: stringifyHref({urlScheme: SCHEME_BY_TYPE.PhoneLink, content: phoneNumber})
        }),
        unpack: (attributes: Attributes, {href}: ParsedAttributes): PhoneLink | undefined => {
            delete attributes.href

            return {phoneNumber: href.content, type: 'PhoneLink'}
        }
    },
    WhatsAppLink: {
        pack: ({phoneNumber}: WhatsAppLink): Attributes => ({
            href: stringifyHref({urlScheme: SCHEME_BY_TYPE.WhatsAppLink, content: phoneNumber})
        }),
        unpack: (attributes: Attributes, {href}: ParsedAttributes): WhatsAppLink | undefined => {
            delete attributes.href

            return {phoneNumber: href.content, type: 'WhatsAppLink'}
        }
    }
}

const pack = (link: Link): Attributes | undefined => (HANDLERS as Record<string, any>)[link.type]?.pack(link)

const unpack = (attributes: Attributes): Link | undefined => {
    const {href, rel, target} = attributes
    if (!_.isString(href)) {
        return
    }

    const parsedHref = parseHref(href)
    const parsedAttributes = {
        href: parseHref(href),
        rel: parseRel(rel),
        target
    }

    const type = parsedHref?.urlScheme && TYPE_BY_SCHEME[parsedHref.urlScheme]
    const handler = (type && (HANDLERS as Record<string, any>)[type]) ?? HANDLERS.ExternalLink
    const link = handler.unpack(attributes, parsedAttributes)
    if (link) {
        return link
    }
}

export const linkReferenceUtil = {
    pack,
    unpack
}
